<template>
  <div
    id="installation-overlay"
    class="overlay overlay--opened"
    @keyDown.prevent="onKeyDown()"
    >
    <div class="overlay--image has-dark-background">
      <overlayWorkDetail
        v-if="overlayDetailOpened"
        ref="overlayDetail"
        :ID="displayedWork.ID"
        :Title="displayedWork.Title"
        :Artist="displayedWork.Artist"
        :DisplayDate="displayedWork.DisplayDate"
        :ImageURL="displayedWork.ImageURL"
        :src-element="selectedMatchIndicator"
        @onClickCloseOverlayWorkDetail="onClickCloseOverlayWorkDetail"
        />

      <controllerGroup
        :isLoading="isLoading"
        :title="slideshowTitle"
        :currentIndex="currentIndex"
        :slideLength="slides.length"
        gtm="installation image"
        @close="$emit('close')"
        @previous="previous()"
        @next="next()"
        />

      <div class="overlay__content-mask--upper">
        <div class="overlay__content-mask--lower--right">
          <div class="overlay__content-container spinner">
            <div class="overlay__content">
              <div
                id="overlay-top"
                :class="spinnerClass"
                >
                <picture class="picture--center">
                  <img
                    ref="slideImage"
                    class="picture__img--scale"
                    data-overlay-image="true"
                    data-overlay-prevent-close="true"
                    data-prevent-context-menu="true"
                    data-toggle-controls="true"
                    draggable="false"
                    :src="currentImage"
                    onerror="window.MoMA.onImageError(this, 1)"
                    loading="lazy"
                    @load="onImgLoad"
                  >
                </picture>
                <matchIndicators
                  v-if="hasIdentifiedWorks && showMatchIndicators"
                  :works="identifiedWorks"
                  :height="height"
                  :width="width"
                  @onClickIdentifiedWork="onClickIdentifiedWork"
                  />
              </div>

              <slideTitle
                :title="slideTitle"
                />

              <section class="uneven-columns--overlay">
                <div class="uneven-columns__first" />
                <div class="uneven-columns__second">
                  <div class="overlay__caption">
                    <span
                      data-overlay-prevent-close
                      class="overlay__caption__text balance-text"
                      v-html="slideCaption"
                      />
                  </div>
                </div>
                <div class="uneven-columns__third">
                  <ul class="supplemental">
                    <li
                      class="supplemental__item $color/white $color/background:black $typography/size:medium"
                      data-overlay-prevent-close
                      >
                      <MoMAlink
                        v-if="!isLoading"
                        :external="false"
                        :linkUrl="mdeUrl"
                        text="Learn more about this exhibition"
                        gtm="clicks exhibition link"
                      />
                    </li>
                  </ul>
                </div>
              </section>

              <workGrid
                v-if="hasIdentifiedWorks"
                :works="identifiedWorks"
                />

              <collapsibleBlockSection
                :hasIdentifiedWorks="hasIdentifiedWorks"
                />

              <footerOverlay />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import controllerGroup from './../slideshows-vue/controllers/controller-group.vue';
  import workGrid from './work-grid.vue';
  import MoMAlink from './../shared/links/link.vue';
  import collapsibleBlockSection from '../shared/blocks/collapsible-block-section.vue';
  import footerOverlay from './footer-overlay.vue';
  import matchIndicators from './match-indicators.vue';
  import overlayWorkDetail from './overlay-work-detail.vue';
  import { MoMABalanceText } from 'sol';
  import slideMixins from "./../slideshows-vue/new-mixins.js";
  import slideTitle from "../slideshows-vue/slides/slide-title.vue";

  export default {
    components: {
      controllerGroup,
      MoMAlink,
      collapsibleBlockSection,
      workGrid,
      footerOverlay,
      matchIndicators,
      overlayWorkDetail,
      slideTitle
    },

    mixins: [
      slideMixins
    ],

    props: {
      objectId: {type: Number, required: true },
      index: {type: Number, required: true },
      installationSource: {type: String, required: true },
    },

    data: function() {
      return {
        slides: [],
        isLoading: true,
        height: 0,
        width: 0,
        currentIndex: this.index,
        imageLoaded: false,
        displayedWork: null,
        overlayDetailOpened: false,
        balanceText: new MoMABalanceText(),
        showMatchIndicators: false,
        selectedMatchIndicator: null,
      };
    },

    computed: {
      hasPreviousSlide() {
        return !this.isLoading && this.currentIndex > 0 ;
      },

      hasNextSlide() {
        return !this.isLoading && (this.currentIndex < this.slides.length - 1);
      },

      hasIdentifiedWorks() {
        return !this.isLoading && this.slides[this.currentIndex].Objects.length > 0;
      },

      identifiedWorks() {
        if (this.isLoading) {
          return [];
        }

        return this.slides[this.currentIndex].Objects;
      },

      currentImage() {
        if (this.isLoading) {
          return "";
        }

        return this.slides[this.currentIndex].ImageURL;
      },

      slideshowTitle() {
        if (!this.isLoading) {
          return this.slides[this.currentIndex].Exhibition.Title;
        }
        return "";
      },

      slideTitle() {
        if (!this.isLoading) {
          // note: <em> here is to negate the ems' coming from tms data
          return `<em>${this.slides[this.currentIndex].Title}</em>`;
        }

        return "";
      },

      slideCaption() {
        if (!this.isLoading) {
          return this.slides[this.currentIndex].Caption;
        }

        return "";
      },

      mdeUrl() {
        if (!this.isLoading) {
          return `${this.slides[this.currentIndex].Exhibition.BaseURL}${this.slides[this.currentIndex].Exhibition.ID}`;
        }
        return "#";
      },
    },

    methods: {
      fetchSlides: async function () {
        const url = `/${this.installationSource}/${this.objectId}/installation_images.json`;
        await fetch(url)
          .then(res => res.json())
          .then(res => {
            this.slides = res;
          })
          .then(res => {
            this.updateHistory();
            this.isLoading= false;
          })
          .catch(error => {
            console.log(url + ' failed with error: ',   error);
          });
      },

      customReset() {
        this.showMatchIndicators = false;
      },

      onImgLoad(event) {
        this.height =  Math.round(event.target.height);
        this.width =  Math.round(event.target.width);
        let imageUrl = `https://www.moma.org${this.slides[this.currentIndex].ImageURL}`;
        this.balanceText.balance(this.$el.getElementsByClassName('balance-text'));
        this.$emit('setMetaImage', imageUrl);
        this.imageLoaded = true;
        setTimeout(() => {
          this.showMatchIndicators = true;
        }, 200);
      },

      onClickIdentifiedWork(workIndex, srcElement) {
        this.selectedMatchIndicator = srcElement;
        this.overlayDetailOpened = true;
        this.displayedWork =  this.slides[this.currentIndex].Objects[workIndex];
      },

      onClickCloseOverlayWorkDetail() {
        this.overlayDetailOpened = false;
      },


      updateHistory() {
        const params = new URLSearchParams(location.search);
        params.set("installation_image_index", this.currentIndex);

        if (this.installationSource === "collection/works") {
          this.updateSovHistory(params);
        } else {
          this.updateMdeHistory(params);
        }
      },

      updateMdeHistory(params) {
        window.history.replaceState({
          context: "installationImageOverlayOnMDE",
          exhibitionId: this.objectId,
          installationImageId: this.slides[this.currentIndex].ID,
        }, "", `${location.pathname}?${params}`);
      },

      updateSovHistory(params) {
        window.history.replaceState({
          context: "installationImageOverlayOnSOV",
          exhibitionId: this.slides[this.currentIndex].Exhibition.ID,
          installationImageId: this.slides[this.currentIndex].ID,
          workId: this.objectId,
        }, "", `${location.pathname}?${params}`);
      },

      todo() {
        //possibly add better transistion for image loaded - tbd if necessary
      }
    }
  };
</script>


